import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

interface Options {
  /**
   * Identity of the chat currently on screen (URL param(s)). Navigating to
   * another chat WITHOUT a new router-state target abandons a pending jump;
   * a change also skips one consume pass so a jump never runs against the
   * previous chat's still-mounted state (same Route → no remount).
   */
  chatKey: string | undefined;
  /** True when the CURRENT chat's messages are loaded and jumpable. */
  ready: boolean;
  /**
   * Load older pages until the message is present in the list state.
   * Resolves true when found. Exceptions are treated as "not found".
   */
  loadUntil: (messageId: string) => Promise<boolean>;
}

/**
 * Jump-to-message controller shared by 1-on-1 and group chats.
 *
 * Sources of a jump target:
 *  - router state `{ highlightMessageId }` (global-search result click) —
 *    captured into a ref because slug redirects and same-Route param changes
 *    can drop `location.state` before the chat finishes loading;
 *  - direct `jumpToMessage(id)` calls (reply-quote click, pinned bar).
 *
 * On success the target message is highlighted for 3 s through the existing
 * `highlightMessageId` scroll+pulse infrastructure in MessagesListView.
 */
export default function useJumpToMessage({ chatKey, ready, loadUntil }: Options) {
  const history = useHistory();
  const location = useLocation();
  const stateJumpId = (location.state as { highlightMessageId?: string } | undefined)?.highlightMessageId;

  const [jumpHighlightId, setJumpHighlightId] = useState<string | undefined>(undefined);
  // Bumped whenever the consume effect must re-evaluate outside its normal
  // deps: after a generation-guard skip (the parent's `ready` may never
  // toggle when the next chat hydrates from a warm in-memory snapshot in the
  // same batched commit) and when a new target arrived mid-flight.
  const [consumeTick, setConsumeTick] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingJumpRef = useRef<string | undefined>(stateJumpId);
  const inFlightRef = useRef(false);
  // `loadUntil` is recreated by parents on every render — keep it in a ref so
  // jumpToMessage stays referentially stable and the consume effect doesn't
  // re-fire per render.
  const loadUntilRef = useRef(loadUntil);
  loadUntilRef.current = loadUntil;
  // Generation guard: in the commit where navigation lands, `ready` still
  // reflects the PREVIOUS chat's state. Skip that one pass.
  const chatGenRef = useRef(chatKey);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const jumpToMessage = useCallback(async (messageId: string): Promise<boolean> => {
    const found = await loadUntilRef.current(String(messageId)).catch(() => false);
    if (!found) return false;
    setJumpHighlightId(String(messageId));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setJumpHighlightId(undefined), 3000);
    return true;
  }, []);

  // Capture the router-state target.
  useEffect(() => {
    if (stateJumpId) pendingJumpRef.current = stateJumpId;
  }, [stateJumpId]);

  // Navigating to another chat without a new target abandons the pending jump.
  // (Slug redirects preserve router state, so an id→slug alias change of the
  // same chat re-captures the target instead of losing it here.)
  useEffect(() => {
    if (!stateJumpId) pendingJumpRef.current = undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatKey]);

  // Consume: retried on readiness/chat transitions until the jump succeeds.
  useEffect(() => {
    if (chatGenRef.current !== chatKey) {
      // Stale-closure commit right after navigation — this pass still sees
      // the PREVIOUS chat's `ready`. Skip it, but schedule one fresh pass:
      // when the next chat hydrates from a warm snapshot inside the same
      // batched commit, `ready` never toggles and no other dep would re-fire
      // the effect (the jump would silently die).
      chatGenRef.current = chatKey;
      setConsumeTick((n) => n + 1);
      return;
    }
    const pending = pendingJumpRef.current;
    if (!pending || !ready || inFlightRef.current) return;
    inFlightRef.current = true;
    jumpToMessage(pending).then((ok) => {
      inFlightRef.current = false;
      if (ok) {
        // Only clear if no NEWER target arrived while this one was in flight.
        if (pendingJumpRef.current === pending) pendingJumpRef.current = undefined;
        // Strip the router state only AFTER the jump landed. Stripping
        // up-front raced the numeric-id→slug alias redirect: the redirect
        // forwards `history.location.state`, which was already empty, so the
        // invalidation rule mistook the redirect for user navigation and
        // killed the in-flight jump. Read the LIVE location — the pathname
        // may have changed to the slug by now. (A reload mid-jump re-jumps —
        // acceptable deep-link semantics.)
        const s = history.location.state as { highlightMessageId?: string } | undefined;
        if (s?.highlightMessageId) {
          history.replace({ pathname: history.location.pathname, search: history.location.search });
        }
      }
      // A different target queued mid-flight: re-evaluate. (A FAILED jump
      // keeps its pending and retries only on real dep changes — never
      // hot-loops the same id.)
      if (pendingJumpRef.current && pendingJumpRef.current !== pending) {
        setConsumeTick((n) => n + 1);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateJumpId, ready, chatKey, jumpToMessage, consumeTick]);

  return { jumpHighlightId, jumpToMessage, pendingJumpRef };
}
